'use client';

import React, { useCallback, useEffect, useState } from 'react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { useReducedMotion } from 'motion/react';
import cardDefaultImg from '@images/card-default.webp';
import { TrackedAnchor } from '@/components/ancher';
import { TrackedButton } from '@/components/button';
import { TRACKED_EVENT_POSITION } from '@/constants';
import type { EditorsPickItem } from '@/services';
import ChevronIcon from './chevron-icons';

const AUTO_ADVANCE_MS = 5000;

/**
 * 자동 전환은 hover·focus·`prefers-reduced-motion` 에서 멈춘다 — WCAG 2.2 SC 2.2.2 는
 * 5초 넘게 자동으로 움직이는 콘텐츠에 멈출 수단을 요구한다.
 */
export default function EditorsPickCarousel({
  posts,
}: {
  posts: EditorsPickItem[];
}) {
  const t = useTranslations('Blog.main');
  const shouldReduceMotion = useReducedMotion();
  const [index, setIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  // 첫 화면에는 활성 이미지 1장만 올린다 — `opacity-0` 은 뷰포트 안에 배치된 상태라
  // `loading="lazy"` 가 안 먹고, 660×396 3장이 LCP 이미지와 대역폭을 다툰다.
  // 나머지는 마운트 직후(= 첫 페인트 뒤) 붙어서 크로스페이드가 준비된다
  const [mountAllSlides, setMountAllSlides] = useState(false);

  useEffect(() => setMountAllSlides(true), []);

  const total = posts.length;

  // 이전 index 로부터 계산한다(고정값 대입 X) — 같은 렌더 배치에서 두 번 눌려도
  // 두 번 다 같은 index 를 읽고 한 칸만 움직이는 일이 없다
  const advance = useCallback(
    (delta: number) =>
      setIndex(prev => (((prev + delta) % total) + total) % total),
    [total],
  );

  useEffect(() => {
    if (shouldReduceMotion || isPaused || total <= 1) return;

    const timer = setTimeout(() => advance(1), AUTO_ADVANCE_MS);
    return () => clearTimeout(timer);
    // index 는 본문에서 쓰지 않지만 의존성에 남긴다 — 화살표로 index 가 바뀌면 이 effect 가
    // 재실행되며 3초가 처음부터 다시 가야 한다 — 화살표가 자동 전환보다 우선이다
  }, [index, isPaused, shouldReduceMotion, total, advance]);

  if (total === 0) return null;

  const current = posts[index];

  return (
    <section
      aria-roledescription="carousel"
      aria-label={t('editorsPick.title')}
      // hover·focus 동안 자동 전환을 멈춘다. focus 는 캡처가 아니라 버블링으로 충분하다
      // (안쪽 링크·버튼이 포커스를 받으면 여기까지 올라온다)
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onFocus={() => setIsPaused(true)}
      onBlur={() => setIsPaused(false)}
      className="group flex flex-col gap-5 lg:flex-row-reverse lg:items-stretch lg:gap-5 xl:gap-10"
    >
      {/* 이미지도 글로 가는 링크다 — 아래 텍스트 링크와 같은 목적지·같은 label 이라
          `aria-hidden` + `tabIndex={-1}` 로 보조기술·키보드에는 노출하지 않는다.
          (안 그러면 같은 글로 가는 링크가 두 번 읽히고 탭 정지점도 둘이 된다) */}
      <TrackedAnchor
        href={current.link}
        label={`editors_pick_post_${index + 1}`}
        position={TRACKED_EVENT_POSITION.MAIN_CONTENT}
        prefetch
        aria-hidden="true"
        tabIndex={-1}
        className="relative aspect-[660/396] w-full shrink-0 overflow-hidden rounded-[20px] bg-3b-background-light lg:w-[52.38%] lg:self-start"
      >
        {posts.map((post, i) =>
          mountAllSlides || i === index ? (
            <Image
              key={post.link}
              src={post.image || cardDefaultImg}
              alt=""
              fill
              sizes="(min-width: 1024px) 660px, 100vw"
              priority={i === 0}
              // 슬라이드 크로스페이드(opacity)는 300ms, 호버 확대(transform)만 150ms —
              // duration 목록은 transition-[opacity,transform] 의 순서를 따른다
              // 크로스페이드(opacity)는 시안 디졸브 500ms ease-in-out, 호버 확대(transform)는
              // QA 확정값 150ms ease-out — 목록 순서는 transition-[opacity,transform] 를 따른다
              className={`object-cover transition-[opacity,transform] [transition-duration:500ms,150ms] [transition-timing-function:ease-in-out,ease-out] group-hover:scale-[1.1] motion-reduce:transform-none ${
                i === index ? 'opacity-100' : 'opacity-0'
              }`}
            />
          ) : null,
        )}
      </TrackedAnchor>

      {/* 열은 `items-stretch` 로 행 높이(=이미지 높이)까지 늘어나고, `justify-between` 이
          텍스트를 위·화살표를 아래에 붙인다 — 그래서 화살표 하단이 이미지 하단과 맞는다 */}
      <div className="flex min-w-0 flex-1 flex-col gap-5 lg:justify-between lg:gap-7">
        {/* 슬라이드 전환의 디졸브 — 이미지는 겹쳐 두고 크로스페이드하지만, 텍스트는 글마다
            높이가 달라 겹칠 수 없어 key 리마운트 + 페이드 인으로 맞춘다(이미지와 같은 500ms).
            자동 전환은 focus 에서 멈추므로 리마운트가 키보드 포커스를 끊는 경우는 없다 */}
        <TrackedAnchor
          key={index}
          href={current.link}
          label={`editors_pick_post_${index + 1}`}
          position={TRACKED_EVENT_POSITION.MAIN_CONTENT}
          prefetch
          className="focus-ring flex animate-fade-in flex-col items-start gap-2.5 motion-reduce:animate-none lg:gap-4"
        >
          <span className="inline-flex shrink-0 items-center rounded-full bg-3b-background-medium px-3 py-1.5 text-caption font-medium text-3b-text-primary lg:h-8 lg:px-2.5 lg:py-0 lg:text-body2">
            {t('editorsPick.title')}
          </span>
          {/* 제목을 2줄로 고정 클램프한다. CSS 말줄임은 "컨테이너 높이"가
              아니라 "줄 수" 기준이라, 제목 줄 수가 흔들리면 아래 설명의 말줄임 지점도 같이
              흔들린다 — 제목을 고정해야 설명 영역이 항상 168px 로 확정된다 */}
          <span className="line-clamp-2 shrink-0 text-h5 text-3b-text-primary lg:text-h4 lg:text-3b-blue-light-900">
            {current.title}
          </span>
          {/* ⚠️ 설명을 `flex-1` 로 늘리지 말 것 — 상자 높이가 클램프보다 커지면
              `-webkit-line-clamp` 이 무력화되어 6줄에서 말줄임하지 않고 상자 높이에서
              그냥 잘린다(제목이 1줄이면 상자가 212px 가 되어 7.57줄에서 잘렸다).
              내용 높이로 두면 항상 6줄 × 28px = 168px 에서 말줄임된다.
              설명이 비어 있으면 줄을 만들지 않는다 — 캐시에 값이 없을 수 있다 */}
          {current.description ? (
            <span className="line-clamp-3 text-sh2 font-medium text-3b-text-secondary lg:line-clamp-4 lg:text-h6 lg:font-medium lg:text-[#575c66] xl:line-clamp-6">
              {current.description}
            </span>
          ) : null}
        </TrackedAnchor>

        {total > 1 ? (
          <div className="mx-auto flex gap-3 lg:mx-0 lg:gap-5">
            {(['left', 'right'] as const).map(direction => (
              <TrackedButton
                key={direction}
                label={
                  direction === 'left'
                    ? 'editors_pick_prev'
                    : 'editors_pick_next'
                }
                position={TRACKED_EVENT_POSITION.MAIN_CONTENT}
                type="button"
                aria-label={t(
                  direction === 'left'
                    ? 'editorsPick.prev'
                    : 'editorsPick.next',
                )}
                onClick={() => advance(direction === 'left' ? -1 : 1)}
                className="group flex size-10 items-center justify-center rounded-full bg-3b-background-medium text-3b-text-primary transition-colors hover:bg-3b-blue-light-900 hover:text-3b-text-contrast lg:size-12"
              >
                <ChevronIcon direction={direction} />
              </TrackedButton>
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
}
